// Shapes returned by sbly-dash-collector /v1/seo-growth (src/seo-growth/brain).

// The Dosaze stores the SEO system works on; each has its own queue, runs and
// settings (the international stores get new articles only, for now).
export type Region = 'US' | 'UK' | 'CA' | 'AU';

export const REGION_INFO: Record<
  Region,
  { label: string; country: string; site: string; blog: string }
> = {
  US: { label: 'US', country: 'the US', site: 'dosaze.com', blog: 'llms' },
  UK: { label: 'UK', country: 'the UK', site: 'uk.dosaze.com', blog: 'news' },
  CA: {
    label: 'Canada',
    country: 'Canada',
    site: 'ca.dosaze.com',
    blog: 'news',
  },
  AU: {
    label: 'Australia',
    country: 'Australia',
    site: 'au.dosaze.com',
    blog: 'news',
  },
};

export type ItemStatus =
  'proposed' | 'changes_requested' | 'published' | 'rejected' | 'reverted';

export const STATUS_LABEL: Record<ItemStatus, string> = {
  proposed: 'To review',
  changes_requested: 'Sent back',
  published: 'Published',
  rejected: 'Rejected',
  reverted: 'Undone',
};

// "Published" reads oddly for a change to an existing article, which is
// applied, not published.
export function statusLabel(item: {
  status: ItemStatus;
  opportunity: { type: string };
}) {
  if (item.status === 'published' && item.opportunity.type !== 'new_article')
    return 'Live';
  return STATUS_LABEL[item.status];
}

export type ActionType = 'new_article' | 'title_meta' | 'refresh' | 'merge';

export const TYPE_LABEL: Record<ActionType, string> = {
  new_article: 'New article',
  title_meta: 'Title & description rewrite',
  refresh: 'Article refresh',
  merge: 'Duplicate merge',
};

export type ArticleFormat = 'buying_guide' | 'how_to' | 'comparison';

export const FORMAT_LABEL: Record<ArticleFormat, string> = {
  buying_guide: 'Buying guide',
  how_to: 'How-to',
  comparison: 'Comparison',
};

export type Opportunity =
  | NewArticleOpportunity
  | TitleMetaOpportunity
  | RefreshOpportunity
  | MergeOpportunity;

export interface NewArticleOpportunity {
  type: 'new_article';
  // International stores: an adaptation of a US article, or a new topic.
  kind?: 'adaptation' | 'new';
  source?: {
    articleId: string;
    url: string;
    title: string;
    usClicks28d: number;
  };
  topicKey: string;
  keyword: string;
  variants: string[];
  monthlySearches: number;
  format: ArticleFormat;
  collection: { path: string; label: string };
  score: number;
  evidence: {
    dosazeBestPosition: number | null;
    dosazeImpressions28d: number;
    dosazeClicks28d?: number;
    dosazePages: number;
    keywordSource: 'gsc' | 'paid' | 'idea';
  };
}

// An existing article whose Google title and meta description get rewritten.
export interface TitleMetaOpportunity {
  type: 'title_meta';
  topicKey: string;
  articleId: string;
  url: string;
  articleTitle: string;
  keyword: string; // its biggest search
  variants: string[];
  score: number; // clicks a month to gain at Dosaze's usual rate
  evidence: {
    clicks28d: number;
    impressions28d: number;
    position: number;
    ctr: number;
    benchmarkCtr: number;
    topQueries: {
      query: string;
      clicks: number;
      impressions: number;
      position: number;
    }[];
  };
}

// An existing article whose text gets rewritten (approval needed).
export interface RefreshOpportunity {
  type: 'refresh';
  topicKey: string;
  articleId: string;
  blogId: string;
  url: string;
  articleTitle: string;
  keyword: string;
  variants: string[];
  collection: { path: string; label: string };
  score: number; // clicks a month to gain if it climbs 3 places
  evidence: {
    clicks28d: number;
    impressions28d: number;
    position: number;
    topQueries: {
      query: string;
      clicks: number;
      impressions: number;
      position: number;
    }[];
  };
}

export interface MergeMember {
  articleId: string;
  blogId: string;
  url: string;
  title: string;
  clicks: number;
  impressions: number;
  position: number | null;
}

// A group of our own duplicate articles folded into the one Google shows most;
// the others are unpublished and redirect to it.
export interface MergeOpportunity {
  type: 'merge';
  topicKey: string;
  articleId: string; // the page that stays
  articleIds: string[];
  blogId: string;
  url: string;
  articleTitle: string;
  others: MergeMember[]; // the pages being retired
  keyword: string;
  variants: string[];
  collection: { path: string; label: string };
  score: number;
  evidence: {
    clicks28d: number; // whole group
    impressions28d: number; // whole group
    winnerClicks28d: number;
    winnerImpressions28d: number;
    position: number | null;
    topQueries: {
      query: string;
      clicks: number;
      impressions: number;
      position: number;
    }[];
  };
}

// For a title/meta rewrite only title and metaDescription are set.
export interface Draft {
  title: string;
  metaDescription: string;
  handle: string;
  summary: string;
  bodyHtml: string;
  html: string;
  faq: { question: string; answer: string }[];
  tags: string[];
}

export interface SeoItem {
  id: string;
  region?: Region;
  status: ItemStatus;
  opportunity: Opportunity;
  draft: Draft;
  validation: {
    errors: string[];
    warnings: string[];
    info: string[];
    words: number;
    links: number;
  };
  usage: { costUsd: number; attempts: number };
  revisions: number;
  feedback?: string | null;
  reason?: string | null;
  decidedAt?: string;
  decidedBy?: string | null;
  lastError?: string | null;
  published?: {
    articleId: string;
    url: string;
    handle?: string;
    publishedAt: string;
    simulated?: boolean;
  };
  revertedAt?: string;
  revertedBy?: string | null;
  // Refreshes: what changed and why, and the live version it replaces.
  changeSummary?: string[];
  refreshBefore?: { title: string; bodyHtml: string; updatedAt: string };
  mergeApplied?: {
    winnerWritten: boolean;
    redirects: { url: string; redirectId?: string }[];
    unpublished: string[];
  };
  createdAt: string;
  updatedAt: string;
  // Decision record (absent on drafts made before the decision log).
  runId?: string;
  observed?: Observed;
  hypothesis?: string;
  change?: Change;
  expected?: Expected;
  reviews: Review[];
  rulesApplied: { id: string; text: string }[];
  thread: ThreadEntry[];
}

export interface Observed {
  summary: string;
  facts: { label: string; value: string; source: string }[];
}

export interface Change {
  summary: string;
  fields: { field: string; before?: string; after: string }[];
}

export interface Checkpoint {
  day: number;
  window: string;
  position: { low: number; mid: number; high: number };
  low: number;
  mid: number;
  high: number;
  // page_ctr only: the rates above as clicks at today's impressions
  clicks?: { low: number; mid: number; high: number };
}

export interface Expected {
  metric: 'page_clicks' | 'page_ctr';
  label: string;
  baseline: number; // clicks, or click-through rate for page_ctr
  baselineClicks?: number;
  checkpoints: Checkpoint[];
  method: string;
}

export type Verdict =
  | 'on_track'
  | 'not_yet'
  | 'behind'
  | 'worked'
  | 'did_not_work'
  | 'inconclusive';

export const VERDICT_LABEL: Record<Verdict, string> = {
  on_track: 'On track',
  not_yet: 'Too early to tell',
  behind: 'Behind so far',
  worked: 'Worked',
  did_not_work: 'Didn’t work',
  inconclusive: 'Inconclusive',
};

export const VERDICT_COLOR: Record<
  Verdict,
  'success' | 'error' | 'warning' | 'info' | 'default'
> = {
  on_track: 'info',
  not_yet: 'default',
  behind: 'warning',
  worked: 'success',
  did_not_work: 'error',
  inconclusive: 'warning',
};

export interface Review {
  day: number;
  dueDate: string;
  from: string;
  to: string;
  status: 'scheduled' | 'done' | 'cancelled';
  measuredAt?: string;
  actual?: {
    clicks: number;
    impressions: number;
    position: number | null;
    topicClicks: number;
    topicImpressions: number;
    orders: number;
    revenueUsd: number;
  };
  verdict?: Verdict | null;
  note?: string;
}

export interface ThreadEntry {
  at: string;
  question: string;
  answer: string;
  suggestedRule: string;
  askedBy: string | null;
}

export interface Rule {
  id: string;
  text: string;
  active: boolean;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export type CandidateOutcome =
  'drafted' | 'excluded_by_rule' | 'failed' | 'waiting' | 'same_topic';

export const OUTCOME_LABEL: Record<CandidateOutcome, string> = {
  drafted: 'Drafted',
  excluded_by_rule: 'Excluded by a rule',
  failed: 'Draft failed',
  waiting: 'Waiting',
  same_topic: 'Same topic as today’s draft',
};

export const SNIPPET_SKIP_LABEL: Record<string, string> = {
  too_new: 'Published less than 8 weeks ago',
  few_impressions: 'Google shows it fewer than 300 times a month',
  beyond_page_two: 'Ranks below page two',
  ctr_already_fine: 'Already gets Dosaze’s usual click rate',
  recently_changed: 'Rewritten in the last 8 weeks',
  duplicate: 'In a group of our own duplicates (the merge step handles it)',
};

export const REFRESH_SKIP_LABEL: Record<string, string> = {
  too_new: 'Published less than 90 days ago',
  few_impressions: 'Google shows it fewer than 1,000 times a month',
  near_top: 'Already in the top 3',
  beyond_page_two: 'Ranks below page two',
  duplicate: 'In a group of our own duplicates (the merge step handles it)',
  no_product_fit: 'Not about something Dosaze sells',
  recently_changed: 'Changed recently; waiting for its review',
  small_gain: 'Climbing a few places wouldn’t add clicks',
};

export const MERGE_SKIP_LABEL: Record<string, string> = {
  recently_changed:
    'Some of its articles changed recently; waiting for their reviews',
  no_product_fit: 'Not about something Dosaze sells',
};

export const SKIP_LABEL: Record<string, string> = {
  not_a_target_search: 'Brand, competitor or unrecognised search',
  no_product_fit: 'Not about something Dosaze sells',
  already_queued: 'Topic already proposed, published or rejected',
  ranks_already: 'A Dosaze page already ranks in the top 10',
  crowded: 'Several Dosaze pages already compete (for the merge step)',
  existing_article: 'An existing article already covers it',
  not_sold_here: 'This store sells nothing it is about',
  no_local_demand: 'Too few searches in this country (US article not adapted)',
};

export interface RunReview {
  itemId: string;
  keyword: string;
  title: string;
  day: number;
  verdict: Verdict | null;
  note: string;
}

export interface LearningChange {
  at: string;
  format: ArticleFormat;
  from: number;
  to: number;
  because: string;
}

export interface RunSummary {
  id: string;
  startedAt: string;
  finishedAt?: string;
  quota: number;
  read: {
    keywordSnapshot: string;
    keywords: number;
    gscThrough: string;
    gscQueries: number;
    articles: number;
    products: number;
  };
  reviews: RunReview[];
  learning: LearningChange[];
  redrafted: { itemId: string; keyword: string }[];
  failed: { step: string; error: string; keyword?: string }[];
  costUsd: number;
  drafted: number;
  candidatesConsidered: number;
  titleRewrites?: number;
  questions: number;
}

export interface RunReport extends Omit<
  RunSummary,
  'drafted' | 'candidatesConsidered' | 'questions'
> {
  benchmarks: {
    gscThrough: string;
    ctrCurve: {
      position: number;
      clicks: number;
      impressions: number;
      ctr: number;
      rawCtr: number;
    }[];
    newArticlePosition: Record<
      string,
      { articles: number; p10: number; p50: number; p90: number }
    >;
  };
  rulesActive: { id: string; text: string }[];
  candidates: {
    rank: number;
    topicKey: string;
    keyword: string;
    monthlySearches: number;
    format: ArticleFormat;
    score: number;
    outcome: CandidateOutcome;
    itemId?: string;
    note?: string;
    kind?: 'adaptation' | 'new';
    source?: string; // the US article being adapted
  }[];
  skipped: Skipped;
  titleQuota?: number;
  // Title/meta rewrites (absent on runs before they existed).
  snippets?: {
    considered: number;
    candidates: {
      rank: number;
      articleId: string;
      title: string;
      url: string;
      impressions: number;
      position: number;
      ctr: number;
      benchmarkCtr: number;
      score: number;
      outcome: CandidateOutcome;
      itemId?: string;
      note?: string;
    }[];
    skipped: Skipped;
  };
  refreshQuota?: number;
  mergeQuota?: number;
  // Duplicate merges (absent on runs before they existed).
  merges?: {
    groups: number;
    considered: number;
    waitingForApproval: number;
    candidates: {
      rank: number;
      articleId: string;
      title: string;
      url: string;
      pages: number;
      impressions: number;
      clicks: number;
      score: number;
      outcome: CandidateOutcome;
      itemId?: string;
      note?: string;
    }[];
    skipped: Skipped;
  };
  // Article refreshes (absent on runs before they existed).
  refreshes?: {
    considered: number;
    waitingForApproval: number;
    candidates: {
      rank: number;
      articleId: string;
      title: string;
      url: string;
      impressions: number;
      clicks: number;
      position: number;
      score: number;
      outcome: CandidateOutcome;
      itemId?: string;
      note?: string;
    }[];
    skipped: Skipped;
  };
  thread: ThreadEntry[];
}

export interface Skipped {
  counts: Record<string, number>;
  top: {
    keyword: string;
    monthlySearches: number;
    reason: string;
    detail: string;
  }[];
}

export interface Tally {
  published: number;
  publishedWithoutChanges: number;
  rejected: number;
  reverted?: number;
  pending: number;
  worked: number;
  did_not_work: number;
  inconclusive: number;
  awaitingVerdict: number;
  judged: number;
  hitRate: number | null;
  approvalRate: number | null;
  cleanApprovalRate: number | null;
  actualVsExpected: number | null;
}

export interface Learning {
  summary: {
    byType: Record<string, Tally>;
    byFormat: Record<string, Tally>;
  };
  weights: Partial<Record<ArticleFormat, number>>;
  history: LearningChange[];
  lastAdjustedAt: string | null;
}

export interface SeoSettings {
  draftsPerDay: number;
  titleRewritesPerDay: number;
  refreshesPerDay: number;
  mergesPerDay: number;
  autoPublish: {
    new_article: boolean;
    title_meta: boolean;
    refresh: boolean;
    merge: boolean;
  };
}

export interface TypeStats {
  published: number;
  publishedWithoutChanges: number;
  rejected: number;
  pending: number;
}
